import React from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessProfile } from '../contexts/BusinessProfileContext';
import { useTerms } from '../contexts/TermsContext';
import { TermsAcceptanceModal } from './Terms/TermsAcceptanceModal';
import { Login } from './Auth';
import { userHasRole } from '../utils/authRoles';
import spottoLogo from '../assets/spotto-logo.png';

type ProtectedRouteProps = {
  children: ReactNode;
  /** If true, skips the business profile check (for setup pages) */
  skipProfileCheck?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  skipProfileCheck = false
}) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { hasProfile, isLoading: profileLoading } = useBusinessProfile();
  const { needsTermsAcceptance, isLoading: termsLoading } = useTerms();
  const location = useLocation();

  // SW-95: User.userRoles is the real (nested) shape returned by the API —
  // a flat `user.roles` array never actually exists on it, so this used to
  // always evaluate to false and agents fell through to the business-owner
  // profile-setup gate below. See utils/authRoles.ts.
  const isAgent = userHasRole(user, 'Agent');

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <img
          src={spottoLogo}
          alt="Spotto"
          className="w-24 h-24 animate-pulse"
        />
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <Login />;
  }

  // Agents work under someone else's business — they never own a profile,
  // so the business-profile gate doesn't apply to them.
  if (!skipProfileCheck && !isAgent) {
    // Show loading while checking profile
    if (profileLoading) {
      return (
        <div className="min-h-screen bg-surface-app flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <img
              src={spottoLogo}
              alt="Spotto"
              className="w-24 h-24 animate-pulse"
            />
            <p className="text-ink-secondary text-sm">Loading your profile...</p>
          </div>
        </div>
      );
    }

    // No business profile - redirect to setup
    if (!hasProfile) {
      // Don't redirect if already on setup page
      if (location.pathname !== '/setup/business-profile') {
        return <Navigate to="/setup/business-profile" replace />;
      }
    }
  }

  // SW-91: versioned consent gate — a mandatory dark-frame modal shown over
  // the portal shell, not a page redirect. Unlike the profile check above,
  // this applies to agents too (an agent stays blocked until the owning
  // business's owner accepts). The backend already resolves
  // RequiresAcceptance=false when the caller has no business yet (e.g. on
  // the profile-setup page), so no extra guard is needed here — the portal
  // renders immediately underneath, and the modal pops in once status
  // resolves, with no extra blocking spinner state for this check.
  return (
    <>
      {children}
      {!termsLoading && needsTermsAcceptance && <TermsAcceptanceModal />}
    </>
  );
};
